
import React from 'react';
import { cn } from '@/lib/utils';

interface ScoreGaugeProps {
  score: number;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
}

const ScoreGauge: React.FC<ScoreGaugeProps> = ({ 
  score, 
  size = 'md',
  showLabel = true,
  className
}) => {
  // Calculate rotation based on score (0-100)
  const rotation = (score / 100) * 180 - 90;
  
  // Determine color based on score range
  const getColor = () => {
    if (score >= 85) return 'bg-green-500';
    if (score >= 70) return 'bg-blue-500';
    if (score >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Size mapping
  const sizeMap = {
    sm: {
      container: 'w-24 h-12',
      gauge: 'w-24 h-24',
      needle: 'h-10 w-1',
      text: 'text-sm'
    },
    md: {
      container: 'w-36 h-18',
      gauge: 'w-36 h-36',
      needle: 'h-14 w-1.5',
      text: 'text-lg'
    },
    lg: {
      container: 'w-48 h-24',
      gauge: 'w-48 h-48',
      needle: 'h-20 w-2',
      text: 'text-2xl'
    },
  };

  return (
    <div className={cn("relative flex flex-col items-center", className)}>
      <div className={cn("relative overflow-hidden", sizeMap[size].container)}>
        <div className={cn(
          "absolute rounded-t-full bg-gray-200 bottom-0 left-0 right-0", 
          sizeMap[size].gauge
        )}></div>
        
        {/* Score ranges */}
        <div className="absolute bottom-0 left-0 right-0 flex rounded-t-full overflow-hidden" style={{height: '100%'}}>
          <div className="w-1/4 h-full bg-red-400 opacity-50"></div>
          <div className="w-1/4 h-full bg-yellow-400 opacity-50"></div>
          <div className="w-1/4 h-full bg-blue-400 opacity-50"></div>
          <div className="w-1/4 h-full bg-green-400 opacity-50"></div>
        </div>
        
        {/* Gauge needle */}
        <div 
          className={cn("absolute bottom-0 left-1/2 -translate-x-1/2 bg-gray-800 rounded origin-bottom", sizeMap[size].needle)}
          style={{ transform: `rotate(${rotation}deg)` }}
        ></div>
        
        {/* Center point of gauge */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3 h-3 bg-gray-800 rounded-full"></div>
      </div>
      
      {showLabel && (
        <div className={cn("mt-2 font-bold text-center", sizeMap[size].text, getColor().replace('bg-', 'text-'))}>
          {score}
        </div>
      )}
    </div>
  );
};

export default ScoreGauge;
